import type { UserSession } from '@fleetops/types'
import { hasMinimumRole } from './rbac'

// Firestore caps `in` / `not-in` / `array-contains-any` at 30 values (raised
// from 10 in 2023). Three hooks used to hardcode 10 and `.slice(0, 10)` the
// rest away: a user assigned to twelve fleets simply never saw two of them,
// with no error anywhere to notice — the same class of silent loss as the
// maintenance window. Chunking removes the ceiling entirely.
export const FLEET_IN_LIMIT = 30

/**
 * The fleet ids a session's queries must be restricted to, or `null` when the
 * session may read the whole tenant.
 *
 * Mirrors canAccessFleet() in firestore.rules, which is what actually enforces
 * this — a query that is not provably within the caller's fleets is denied
 * outright, so these filters are not decoration.
 *
 * `''` is included deliberately: records created before a fleet was picked
 * (the forms default fleetId to '') count as unassigned, and the rules treat
 * those as visible to everyone. Leaving it out would filter away exactly the
 * documents the rules do allow.
 */
export function fleetScopeOf(session: UserSession | null | undefined): string[] | null {
  if (!session) return null
  if (hasMinimumRole(session.role, 'fleet_manager')) return null
  if (!session.assignedFleetIds?.length) return null
  return [...session.assignedFleetIds, '']
}

/** Splits a scope into `in`-sized chunks, one query each. */
export function chunkFleetIds(ids: string[]): string[][] {
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += FLEET_IN_LIMIT) {
    chunks.push(ids.slice(i, i + FLEET_IN_LIMIT))
  }
  return chunks
}

/**
 * The chunks to run for a session: one `null` chunk means "no fleet filter".
 * Callers run one listener per entry and merge the results.
 */
export function fleetQueryChunks(session: UserSession | null | undefined): (string[] | null)[] {
  const scope = fleetScopeOf(session)
  return scope ? chunkFleetIds(scope) : [null]
}

/** Stable dependency key — the scope is rebuilt on every render. */
export function fleetScopeKey(session: UserSession | null | undefined): string {
  return fleetScopeOf(session)?.join(',') ?? ''
}
